import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const alertVariants = cva(
  `ui-relative ui-w-full ui-rounded-lg ui-border-none ui-p-4 [&>svg~*]:ui-pl-7 [&>svg+div]:ui-translate-y-[-3px] [&>svg]:ui-absolute [&>svg]:ui-left-4 [&>svg]:ui-top-1/2 [&>svg]:ui-translate-y-[-50%]`,
  {
    variants: {
      variant: {
        default: `ui-bg-background ui-text-foreground`,
        info: `ui-bg-blue-50 ui-text-blue-600`,
        success: `ui-bg-green-50 ui-text-green-800`,
        warning: `ui-bg-yellow-50 ui-text-yellow-600`,
        error: `ui-bg-red-50 ui-text-red-600`,
        destructive: `ui-border-destructive/50 ui-text-destructive dark:ui-border-destructive [&>svg]:ui-text-destructive`
      }
    },
    defaultVariants: {
      variant: `default`
    }
  }
)

export type AlertVariant = VariantProps<typeof alertVariants>['variant']

const Alert = ({
  ref,
  className,
  variant,
  ...props
}: React.ComponentPropsWithRef<'div'> & {
  variant: AlertVariant
}) => <div ref={ref} role='alert' className={cn(alertVariants({ variant, className }))} {...props} />
Alert.displayName = `Alert`

const AlertTitle = ({ ref, className, children, ...props }: React.ComponentPropsWithRef<'h5'>) => (
  <h5 ref={ref} className={cn(`ui-font-medium ui-text-sm ui-ml-2 ui-leading-none ui-tracking-tight`, className)} {...props}>
    {children}
  </h5>
)
AlertTitle.displayName = `AlertTitle`

const AlertDescription = ({ ref, className, ...props }: React.ComponentPropsWithRef<'p'>) => (
  <div ref={ref} className={cn(`ui-text-xs ui-ml-2 ui-mt-1 [&_p]:ui-leading-relaxed`, className)} {...props} />
)
AlertDescription.displayName = `AlertDescription`

export { Alert, AlertTitle, AlertDescription }
